import { NextSeo } from 'next-seo'
import { SITE_URL } from '@/config'

type Props = {
  title?: string
  description?: string
  url?: string
  params?: string
  twitterAccount?: string
  isPrivate?: boolean
  type?: 'website' | 'profile' | 'article'
  profile?: {
    firstName?: string
    lastName?: string
    username?: string
  }
}

const DEFAULT_TITLE = '音楽のクリエイターに楽曲制作を依頼できる | ONLIVE Studio'
const DEFAULT_DESCRIPTION = '音楽制作に特化したマッチングサービス。公開依頼でクリエイターから提案を受け取るか、クリエイターを検索して直接依頼。作詞・作曲・ミックス・レコーディングなど幅広く対応。'
const DEFAULT_IMAGE = `${SITE_URL}/api/og`

function formatTitle(title: string): string {
  return title ? `${title} | ONLIVE Studio` : DEFAULT_TITLE
}

function formatDescription(description: string): string {
  if (!description) return DEFAULT_DESCRIPTION
  // Remove newlines and limit to 160 characters for optimal SERP display
  const cleaned = description.replace(/\r?\n|\r/g, ' ').trim()
  return cleaned.length > 160 ? `${cleaned.slice(0, 157)}...` : cleaned
}

function formatImageUrl(params?: string): string {
  if (!params) return DEFAULT_IMAGE
  if (params.startsWith('https')) return params
  return `${SITE_URL}/api/og${params}`
}

function formatCanonicalUrl(url?: string): string | undefined {
  if (url === undefined) return undefined
  const cleanUrl = url.replace(/^\/+/, '')
  return `${SITE_URL}/${cleanUrl}`
}

const SEO = ({
  title = '',
  description = '',
  url,
  params,
  twitterAccount,
  isPrivate,
  type = 'website',
  profile,
}: Props) => {
  const seoTitle = formatTitle(title)
  const seoDescription = formatDescription(description)
  const imageUrl = formatImageUrl(params)
  const seoUrl = formatCanonicalUrl(url)

  return (
    <NextSeo
      title={seoTitle}
      description={seoDescription}
      canonical={seoUrl}
      noindex={isPrivate}
      nofollow={isPrivate}
      openGraph={{
        type,
        url: seoUrl,
        title: seoTitle,
        description: seoDescription,
        siteName: 'ONLIVE Studio',
        images: [{
          url: imageUrl,
          width: 1200,
          height: 630,
          alt: title || 'ONLIVE Studio',
        }],
        locale: 'ja_JP',
        profile: type === 'profile' ? profile : undefined,
      }}
      twitter={{
        handle: twitterAccount,
        site: '@onlivestudio',
        cardType: 'summary_large_image',
      }}
    />
  )
}

export default SEO
